import { NavigationOverlayEmptyState } from "./NavigationOverlayEmptyState";
import { getNotificationAppearance } from "../../../components/notifications/notificationAppearance.shared";
import type {
  AnnouncementListResponseType,
  NotificationListResponseType,
} from "@sellar-io/api-v2/types";
import { Icon, type IconType } from "@sellar-io/components";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useNavigateToChild } from "../../../hooks/useParentNavigation";
import { useAnnouncementList } from "../../../hooks/api/v2/announcement/useAnnouncementList";
import { useAnnouncementMarkAllAsRead } from "../../../hooks/api/v2/announcement/useAnnouncementMarkAllAsRead";
import { useAnnouncementReadCreate } from "../../../hooks/api/v2/announcementRead/useAnnouncementReadCreate";
import { useNotificationList } from "../../../hooks/api/v2/notification/useNotificationList";
import { useNotificationMarkAllAsRead } from "../../../hooks/api/v2/notification/useNotificationMarkAllAsRead";
import { useNotificationReadCreate } from "../../../hooks/api/v2/notificationRead/useNotificationReadCreate";
import { hasBusinessMembership } from "../../../hooks/api/v2/apiV2Request";
import { useFirebaseUser } from "../../../hooks/useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import {
  getAnnouncementNavigationUrl,
  getNotificationNavigationUrl,
} from "../../../components/notifications/notificationNavigation.helper";
import { NOTIFICATIONS } from "../../../constants/routes";
import {
  NOTIFICATION_BADGE_LIMIT,
  useRetailerNotificationSummary,
} from "../useRetailerNotificationIndicator";
import {
  NavigationOverlayBody,
  NavigationOverlayHeader,
  NavigationOverlayFooter,
} from "./NavigationOverlayLayout";
import { NavigationOverlayRow } from "./NavigationOverlayRow";

dayjs.extend(relativeTime);

const PREVIEW_LIMIT = 10;
type Preview =
  | { kind: "notification"; value: NotificationListResponseType[number] }
  | { kind: "announcement"; value: AnnouncementListResponseType[number] };

export const NotificationsNavigationContent = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  if (!hasBusinessMembership({ user, business })) {
    return (
      <>
        <NavigationOverlayHeader title="Notifications" />
        <p className="py-4 text-sm text-zinc-500">
          Notifications are only available to members of this business.
        </p>
      </>
    );
  }
  return <MemberNotifications key={business?.id} onClose={onClose} />;
};

const MemberNotifications = ({ onClose }: { onClose: () => void }) => {
  const summary = useRetailerNotificationSummary();
  const notifications = useNotificationList({
    query: [{ type: "priority", value: "high", modifier: "EQUALS" }],
    limit: PREVIEW_LIMIT,
    sortBy: "createdAt",
    sortDir: "desc",
  });
  const announcements = useAnnouncementList({
    limit: PREVIEW_LIMIT,
    sortBy: "createdAt",
    sortDir: "desc",
  });
  const markNotifications = useNotificationMarkAllAsRead();
  const markAnnouncements = useAnnouncementMarkAllAsRead();
  const [markFailed, setMarkFailed] = useState(false);
  const marking = markNotifications.isPending || markAnnouncements.isPending;
  const failed =
    notifications.isError ||
    announcements.isError ||
    summary.notifications.isError ||
    summary.announcements.isError;
  const loading = notifications.isLoading || announcements.isLoading;
  const previews: Preview[] = [
    ...(notifications.data?.pages.flat() ?? []).map((value) => ({
      kind: "notification" as const,
      value,
    })),
    ...(announcements.data?.pages.flat() ?? []).map((value) => ({
      kind: "announcement" as const,
      value,
    })),
  ];
  previews.sort(
    (a, b) =>
      new Date(b.value.createdAt).getTime() -
      new Date(a.value.createdAt).getTime(),
  );
  const markAll = async () => {
    setMarkFailed(false);
    const results = await Promise.allSettled([
      markNotifications.mutateAsync({}),
      markAnnouncements.mutateAsync(),
    ]);
    setMarkFailed(results.some((result) => result.status === "rejected"));
  };
  let badgeCount: number | string | undefined;
  if (summary.unreadCount) {
    badgeCount =
      summary.unreadCount > NOTIFICATION_BADGE_LIMIT
        ? `${NOTIFICATION_BADGE_LIMIT}+`
        : summary.unreadCount;
  }
  return (
    <>
      <NavigationOverlayHeader
        title="Notifications"
        count={badgeCount}
        countClassName="bg-[#8c23c7] text-[11px] leading-4"
        action={
          <button
            type="button"
            disabled={marking || !summary.unreadCount}
            onClick={markAll}
            className="min-h-6 cursor-pointer rounded font-medium text-[#8c23c7] hover:font-bold focus-visible:outline-2 focus-visible:outline-purple-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {marking ? "Marking…" : "Mark all read"}
          </button>
        }
      />
      <NavigationOverlayBody>
        {loading && (
          <p role="status" className="py-4 text-sm text-zinc-500">
            Loading notifications…
          </p>
        )}
        {failed && (
          <div role="alert" className="text-sm text-zinc-500">
            Unable to refresh notifications.{" "}
            <button
              type="button"
              className="text-purple-700 underline"
              onClick={() => {
                notifications.refetch();
                announcements.refetch();
                summary.notifications.refetch();
                summary.announcements.refetch();
              }}
            >
              Try again
            </button>
          </div>
        )}
        {markFailed && (
          <p role="alert" className="text-sm text-red-700">
            Some notifications could not be marked as read. Please try again.
          </p>
        )}
        {!loading &&
          (previews.length === 0 || summary.unreadCount === 0) &&
          !failed && <NavigationOverlayEmptyState kind="notifications" />}
        {(summary.unreadCount === 0 ? [] : previews).map((preview) => (
          <NotificationPreview
            key={`${preview.kind}:${preview.value.id}`}
            preview={preview}
            onClose={onClose}
          />
        ))}
        {summary.unreadCount !== 0 &&
          (notifications.hasNextPage || announcements.hasNextPage) && (
            <button
              type="button"
              disabled={
                notifications.isFetchingNextPage ||
                announcements.isFetchingNextPage
              }
              onClick={() => {
                if (notifications.hasNextPage) {
                  notifications.fetchNextPage();
                }
                if (announcements.hasNextPage) {
                  announcements.fetchNextPage();
                }
              }}
              className="shrink-0 rounded-md p-2.5 text-sm text-purple-700 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {notifications.isFetchingNextPage ||
              announcements.isFetchingNextPage
                ? "Loading…"
                : "Load more notifications"}
            </button>
          )}
      </NavigationOverlayBody>
      <NavigationOverlayFooter>
        <NavigationOverlayRow
          variant="footer"
          to={NOTIFICATIONS}
          trailing={<Icon icon="CHEVRON_RIGHT" size="16px" />}
        >
          View all notifications
        </NavigationOverlayRow>
      </NavigationOverlayFooter>
    </>
  );
};

const getPreviewIcon = (
  preview: Preview,
): { icon: IconType; className: string } => {
  if (preview.kind === "announcement") {
    return {
      icon: "BULLHORN_OUTLINE",
      className: "bg-purple-100 text-purple-700",
    };
  }
  return getNotificationAppearance(preview.value.subtype);
};

const NotificationPreview = ({
  preview,
  onClose,
}: {
  preview: Preview;
  onClose: () => void;
}) => {
  const notificationRead = useNotificationReadCreate();
  const announcementRead = useAnnouncementReadCreate();
  const navigate = useNavigate();
  const navigateToChild = useNavigateToChild();
  const unread =
    preview.kind === "notification"
      ? !preview.value.NotificationRead
      : !preview.value.AnnouncementRead;
  const { icon, className } = getPreviewIcon(preview);
  const follow = () => {
    if (preview.kind === "notification") {
      navigateToChild(getNotificationNavigationUrl(preview.value));
    } else {
      navigate(getAnnouncementNavigationUrl(preview.value));
    }
    onClose();
  };
  const open = () => {
    if (!unread) {
      follow();
      return;
    }
    if (preview.kind === "notification") {
      notificationRead.mutate(
        { NotificationId: preview.value.id },
        { onSuccess: follow },
      );
    } else {
      announcementRead.mutate(
        { AnnouncementId: preview.value.id },
        { onSuccess: follow },
      );
    }
  };
  return (
    <div className="shrink-0">
      <NavigationOverlayRow
        variant="preview"
        unread={unread}
        pending={notificationRead.isPending || announcementRead.isPending}
        onClick={open}
        titleClassName={unread ? "font-semibold" : "font-normal"}
        description={dayjs(preview.value.createdAt).fromNow()}
        descriptionClassName="text-xs leading-[18px]"
        leading={
          <span
            className={`flex size-10 items-center justify-center rounded-full ${className}`}
          >
            <Icon icon={icon} size="24px" />
          </span>
        }
      >
        {preview.value.title}
      </NavigationOverlayRow>
      {(notificationRead.isError || announcementRead.isError) && (
        <p role="alert" className="px-3 py-1 text-xs text-red-700">
          Unable to open this notification. Please try again.
        </p>
      )}
    </div>
  );
};
